// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, useShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@sc/App/Components/Common/XngZone";
import ProductDetailsSummary from "@sc/App/Components/ProductDetails/ProductDetailsSummary";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { xClassNames } from "@sc/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Change
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    bottomSection?: InjectableCss;
    priceSection?: InjectableCss;
    separator?: InjectableCss;
    imageSection?: InjectableCss;
    actionsWrapper?: InjectableCss;
    stickyWrapper?: InjectableCss;
    headerPage?: MobiusPageProps;
    topSection?: InjectableCss;
    actionsAndWarehouseWrapper?: InjectableCss;
    productShare?: InjectableCss;
    documentaionsAndPropsWrapper?: InjectableCss;
    productInfo?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    badgesZoneWrapper?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

type Props = WidgetProps & HasShellContext;

const ProductDetailsView: React.FC<Props> = props => {
    const { isInShell } = useShellContext();

    const [stickyVisible, setStickyVisible] = useState(false);
    const isShellEditingMood = props.shellContext?.isEditing;
    let descElement: HTMLElement | null = null;

    const stickyHeader = useAppSelector(state => (state as CustomAppState).xHeader.stickyHeader || "138px");

    useEffect(() => {
        document.addEventListener("scroll", handleSticky, { passive: true });
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
        };
    }, []);

    const handleSticky = () => {
        const isSticky = (isSticky: boolean) => {
            setStickyVisible(isSticky);
        };

        if (!descElement) {
            descElement = document.querySelector('[data-test-selector="productDetails_bottomSection"]');
            isSticky(false);
            return;
        }

        if (window.scrollY > parseFloat(stickyHeader) + 10) {
            isSticky(true);
        } else {
            isSticky(false);
        }
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <Hidden above="md">
                <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Details Title" />
                <StyledWrapper {...styles.productNumbersWrapper}>
                    {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                        fields: {},
                    })}
                    {createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                        fields: {},
                    })}
                    {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                        fields: {},
                    })}
                </StyledWrapper>
                <StyledWrapper {...styles.badgesZoneWrapper}>
                    <XngZone contentId={props.id} zoneName="Content3" hint="Zone For Badges" />
                </StyledWrapper>
            </Hidden>
            <StyledWrapper {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                <StyledWrapper {...styles.topSection}>
                    <StyledWrapper {...styles.imageSection}>
                        <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Image" />
                        <XngZone contentId={props.id} zoneName="Content1" hint="Zone For Image Carousel" />
                    </StyledWrapper>
                    <Hidden above="md">
                        <StyledWrapper {...styles.actionsAndWarehouseWrapper}>
                            <StyledWrapper {...styles.actionsWrapper}>
                                <StyledWrapper {...styles.productShare}>
                                    <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Product Share" />
                                </StyledWrapper>
                                <XngZone
                                    contentId={props.id}
                                    zoneName="Content8"
                                    hint="Zone For ProductDetailsActions"
                                />
                            </StyledWrapper>
                            <XngZone
                                contentId={props.id}
                                zoneName="ContentCustomAvailabilityMessage"
                                hint="Zone For Custom Availability Message"
                            />
                            <XngZone contentId={props.id} zoneName="Content9" hint="Zone For Warehouse Availability" />
                        </StyledWrapper>
                    </Hidden>
                    <StyledWrapper {...styles.productInfo}>
                        <Hidden below="lg">
                            <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Product Details Title" />
                            <StyledWrapper {...styles.productNumbersWrapper}>
                                {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                                    fields: {},
                                })}
                                {createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                                    fields: {},
                                })}
                                {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                                    fields: {},
                                })}
                            </StyledWrapper>
                            <StyledWrapper {...styles.badgesZoneWrapper}>
                                <XngZone contentId={props.id} zoneName="Content3" hint="Zone For Badges" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.separator} />
                        </Hidden>
                        <XngZone contentId={props.id} zoneName="Content4" hint="Zone For Product Features" />
                    </StyledWrapper>
                </StyledWrapper>
                <Hidden above="md">
                    <StyledWrapper {...styles.documentaionsAndPropsWrapper}>
                        <XngZone contentId={props.id} zoneName="Content10" hint="Zone For Documentations" />
                        <XngZone contentId={props.id} zoneName="Content11" hint="Zone For prop warning" />
                    </StyledWrapper>
                </Hidden>
                <XngZone
                    contentId={props.id}
                    zoneName="Content7"
                    hint="Zone For Product Description , feature and specifications"
                />
                <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Videos" />
            </StyledWrapper>

            <StyledWrapper {...styles.rightSection} data-test-selector="productDetails_rightColumn">
                <Hidden below="lg">
                    <StyledWrapper {...styles.actionsAndWarehouseWrapper}>
                        <StyledWrapper {...styles.actionsWrapper}>
                            <StyledWrapper {...styles.productShare}>
                                <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Product Share" />
                            </StyledWrapper>
                            <XngZone contentId={props.id} zoneName="Content8" hint="Zone For ProductDetailsActions" />
                        </StyledWrapper>
                        <XngZone
                            contentId={props.id}
                            zoneName="ContentCustomAvailabilityMessage"
                            hint="Zone For Custom Availability Message"
                        />
                        <XngZone contentId={props.id} zoneName="Content9" hint="Zone For Warehouse Availability" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.documentaionsAndPropsWrapper}>
                        <XngZone contentId={props.id} zoneName="Content10" hint="Zone For Documentations" />
                        <XngZone contentId={props.id} zoneName="Content11" hint="Zone For prop warning" />
                    </StyledWrapper>
                </Hidden>

                <XngZone contentId={props.id} zoneName="Content12" hint="Zone For Additional content" />
            </StyledWrapper>

            <StyledWrapper
                {...styles.bottomSection}
                {...{ isInShell }}
                data-test-selector="productDetails_bottomSection"
            >
                <XngZone contentId={props.id} zoneName="Content13" hint="Zone For Related Items Product Carousels" />
                <XngZone contentId={props.id} zoneName="Content14" hint="Zone For Featured Product Carousels" />
            </StyledWrapper>
            <StyledWrapper
                className={xClassNames.append([
                    stickyVisible ? "stickyVisible" : "stickyHidden",
                    isShellEditingMood ? "editingMode" : "",
                ])}
                data-test-selector="productDetails-sticky-header"
                {...styles.stickyWrapper}
            >
                <Page background="common.white" {...styles.headerPage}>
                    <ProductDetailsSummary />
                </Page>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(withIsInShell(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
